<script setup lang="ts">
import { Layout, Search, Building2, Map, LogOut, ShieldCheck } from 'lucide-vue-next';
import { cn } from '@/lib/utils';

const props = defineProps<{
    developers: string[];
    projectsCount: number;
    totalPlansCount: number;
    isAdmin: boolean;
}>();

const searchQuery = defineModel<string>('searchQuery', { default: '' });
const selectedDeveloper = defineModel<string | null>('selectedDeveloper', { default: null });

const emit = defineEmits(['reset-filters', 'logout', 'open-admin']);
</script>

<template>
  <aside class="w-80 bg-slate-900 flex flex-col border-r border-slate-800 shadow-2xl z-20 relative">
    <!-- Architectural Background Decoration -->
    <div class="absolute inset-0 pointer-events-none opacity-5 overflow-hidden">
      <div class="blueprint-grid absolute inset-0"></div>
    </div>

    <div class="p-6 flex items-center gap-4 border-b border-slate-800 group cursor-pointer relative z-10" @click="$emit('reset-filters')">
      <div class="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-all duration-500 shadow-lg shadow-blue-600/20">
         <Layout class="w-7 h-7 text-white" />
      </div>
      <div class="flex flex-col">
        <span class="text-white font-black tracking-widest text-xl leading-none uppercase">Plans<span class="text-blue-500 opacity-80">Hub</span></span>
        <span class="text-[9px] uppercase tracking-[0.3em] text-slate-500 font-black mt-1">Explorateur de projets</span>
      </div>
    </div>

    <nav class="flex-1 py-8 px-5 space-y-10 overflow-y-auto relative z-10">
      <!-- Search Section -->
      <div class="space-y-4">
        <div class="flex items-center justify-between px-2">
          <h3 class="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Rechercher un projet</h3>
          <div class="h-px flex-1 bg-slate-800 ml-4"></div>
        </div>
        <div class="relative group">
          <Search class="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600 group-focus-within:text-blue-400 transition-colors" />
          <input 
            v-model="searchQuery" 
            type="text" 
            placeholder="NOM DU PROGRAMME..."
            aria-label="Rechercher un programme par son nom"
            class="w-full bg-slate-950 border border-slate-800 rounded-2xl py-4 pl-12 pr-4 text-xs font-bold text-white placeholder:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all uppercase tracking-wider"
          />
        </div>
      </div>

      <!-- Developers Filter Section -->
      <div class="space-y-4">
        <div class="flex items-center justify-between px-2">
          <h3 class="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Filtrer par promoteur</h3>
          <div class="h-px flex-1 bg-slate-800 ml-4"></div>
        </div>
        
        <div class="space-y-2">
          <button 
            @click="selectedDeveloper = null"
            aria-label="Tous les promoteurs"
            :aria-pressed="!selectedDeveloper"
            :class="cn('w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all text-[10px] font-black uppercase tracking-widest', 
                       !selectedDeveloper ? 'bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-600/20' : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-700')"
          >
            Tous les promoteurs
            <div v-if="!selectedDeveloper" class="w-1.5 h-1.5 bg-white rounded-full" aria-hidden="true"></div>
          </button>

          <button 
            v-for="dev in developers" 
            :key="dev"
            @click="selectedDeveloper = dev"
            :aria-label="`Filtrer par ${dev}`"
            :aria-pressed="selectedDeveloper === dev"
            :class="cn('w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all text-[10px] font-black uppercase tracking-widest', 
                       selectedDeveloper === dev ? 'bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-600/20' : 'bg-slate-950 border-slate-800 text-slate-500 hover:border-slate-700')"
          >
            {{ dev }}
            <div v-if="selectedDeveloper === dev" class="w-1.5 h-1.5 bg-white rounded-full" aria-hidden="true"></div>
          </button>
        </div>
      </div>

      <!-- Advanced Stats -->
      <div class="p-5 bg-slate-950/50 rounded-[2rem] border border-slate-800/50 space-y-4">
         <div class="flex items-center gap-3">
            <div class="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center border border-blue-500/20">
               <Building2 class="w-4 h-4 text-blue-400" />
            </div>
            <div class="flex flex-col">
               <span class="text-[10px] font-black text-slate-500 uppercase leading-none">Total projets</span>
               <span class="text-lg font-black text-white leading-none mt-1">{{ projectsCount }}</span>
            </div>
         </div>
         <div class="flex items-center gap-3">
            <div class="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center border border-emerald-500/20">
               <Map class="w-4 h-4 text-emerald-400" />
            </div>
            <div class="flex flex-col">
               <span class="text-[10px] font-black text-slate-500 uppercase leading-none">Plans indexés</span>
               <span class="text-lg font-black text-white leading-none mt-1">{{ totalPlansCount }}</span>
            </div>
         </div>
      </div>
    </nav>

    <!-- Bottom Profile -->
    <div class="p-6 border-t border-slate-800 bg-slate-900/50 relative z-10">
      <button 
        class="w-full flex items-center justify-center gap-3 py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 hover:text-white hover:bg-rose-500/10 transition-all border border-slate-800 hover:border-rose-500/20 group"
        aria-label="Déconnexion du système et retour à l'écran de connexion"
        @click="$emit('logout')"
      >
        <LogOut class="w-4 h-4 group-hover:rotate-180 transition-transform duration-500" aria-hidden="true" /> Déconnexion
      </button>

      <button 
        v-if="isAdmin"
        class="w-full mt-4 flex items-center justify-center gap-3 py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] text-blue-500 hover:text-white hover:bg-blue-600/20 transition-all border border-blue-500/20 hover:border-blue-500/40 group"
        aria-label="Accéder à l'administration"
        @click="$emit('open-admin')"
      >
        <ShieldCheck class="w-4 h-4 group-hover:scale-125 transition-transform duration-500" aria-hidden="true" /> Administration
      </button>
    </div>
  </aside>
</template>
